import { useParams } from "react-router";
import {
  ParcelTrackingEvent,
  ParcelTrackingEventType,
  useParcelTrackingEvents,
  useParcelLiveTracking,
} from "@/api/use-parcel-live-tracking";
import { Card } from "@/components/ui/card";
import {
  Loader2,
  Truck,
  MapPin,
  Package,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRight,
  Circle,
  Phone,
  X,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDescription,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { useNavigate } from "react-router";
import { useMemo, useState, useEffect } from "react";
import { BackgroundBeams } from "@/components/ui/background-beams";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { TrackingAuthForm } from "./tracking-auth-form";
import { useQueryClient } from "@tanstack/react-query";
import { OperatorLocation } from "./operator-location";

// Status configuration based on event types
const getStatusConfig = (eventType: ParcelTrackingEventType) => {
  const configs: Record<
    string,
    { color: string; icon: LucideIcon; bgColor: string; textColor: string }
  > = {
    // Pickup Events
    [ParcelTrackingEventType.PARCEL_CREATED]: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.ORDER_PROCESSED]: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.PICKUP_SCHEDULED]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Clock,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.OUT_FOR_PICKUP]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.PICKED_UP]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: CheckCircle2,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.HELD_FOR_PICKUP]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: Clock,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.PICKUP_EXCEPTION]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.PICKUP_CANCELLED]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },

    // Customs Events
    [ParcelTrackingEventType.CLEARED_CUSTOMS]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: CheckCircle2,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.CUSTOMS_DELAY]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },

    // Delivery Events
    [ParcelTrackingEventType.OUT_FOR_DELIVERY]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.DELIVERED]: {
      color: "text-green-600",
      bgColor: "bg-green-50",
      icon: CheckCircle2,
      textColor: "text-green-700",
    },
    [ParcelTrackingEventType.DELIVERY_EXCEPTION]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.DELIVERY_CANCELLED]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },

    // Return Events
    [ParcelTrackingEventType.RETURNED_TO_FACILITY]: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.RETURN_TO_SENDER]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },
    [ParcelTrackingEventType.RETURN_EXCEPTION]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.RETURN_CANCELLED]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },

    // Depot Events
    [ParcelTrackingEventType.DEPOT_RECEIVE_EXCEPTION]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.DEPOT_RECEIVE_CANCELLED]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },
    [ParcelTrackingEventType.DEPOT_SORT_EXCEPTION]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.DEPOT_SORT_CANCELLED]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },
    [ParcelTrackingEventType.DEPOT_DISPATCH_EXCEPTION]: {
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      icon: AlertCircle,
      textColor: "text-orange-700",
    },
    [ParcelTrackingEventType.DEPOT_DISPATCH_CANCELLED]: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: AlertCircle,
      textColor: "text-red-700",
    },
    [ParcelTrackingEventType.ARRIVED_AT_FACILITY]: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.IN_TRANSIT]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.DEPARTED_FACILITY]: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: Truck,
      textColor: "text-gray-900",
    },
    [ParcelTrackingEventType.PROCESSING_AT_FACILITY]: {
      color: "text-gray-400",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
    default: {
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      icon: Package,
      textColor: "text-gray-900",
    },
  };

  return configs[eventType] ?? configs.default;
};

const isFinalStatus = (eventType: ParcelTrackingEventType) => {
  return [
    ParcelTrackingEventType.DELIVERED,
    ParcelTrackingEventType.PICKUP_CANCELLED,
    ParcelTrackingEventType.DELIVERY_CANCELLED,
    ParcelTrackingEventType.RETURN_TO_SENDER,
    ParcelTrackingEventType.RETURN_CANCELLED,
    ParcelTrackingEventType.DEPOT_RECEIVE_CANCELLED,
    ParcelTrackingEventType.DEPOT_SORT_CANCELLED,
    ParcelTrackingEventType.DEPOT_DISPATCH_CANCELLED,
  ].includes(eventType);
};

const renderMockTimelineItems = (
  latestEventType: ParcelTrackingEventType,
  events: ParcelTrackingEvent[]
) => {
  // Only show future events if the current status isn't final
  if (isFinalStatus(latestEventType)) {
    return null;
  }

  // Array to hold mock events
  const mockEvents = [];

  // Calculate future dates for estimated events
  const today = new Date();

  // For delivery, estimate 1-3 days from now based on current status
  const deliveryDate = new Date(today);
  if (latestEventType === ParcelTrackingEventType.OUT_FOR_DELIVERY) {
    // If out for delivery, estimate today with time window
    deliveryDate.setHours(today.getHours() + 2 + Math.floor(Math.random() * 4));
  } else if (latestEventType === ParcelTrackingEventType.IN_TRANSIT) {
    // If in transit, estimate 1-2 days
    deliveryDate.setDate(today.getDate() + 1 + Math.floor(Math.random() * 2));
  } else {
    // Otherwise estimate 2-4 days
    deliveryDate.setDate(today.getDate() + 2 + Math.floor(Math.random() * 3));
  }

  // For pickup, estimate tomorrow
  const pickupDate = new Date(today);
  pickupDate.setDate(today.getDate() + 1);
  pickupDate.setHours(9 + Math.floor(Math.random() * 6)); // Between 9am and 3pm

  // Add delivery mock event if not delivered yet
  if (latestEventType !== ParcelTrackingEventType.DELIVERED) {
    const deliveryWindow = `${deliveryDate.getHours()}:00 - ${deliveryDate.getHours() + 2}:00`;
    const formattedDate = deliveryDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

    mockEvents.push({
      id: "estimated-delivery",
      eventType: ParcelTrackingEventType.DELIVERED,
      eventName: "Delivery",
      eventDescription:
        latestEventType === ParcelTrackingEventType.OUT_FOR_DELIVERY
          ? `Expected today, ${deliveryWindow}`
          : `Expected ${formattedDate}`,
      timestamp: deliveryDate.toISOString(),
    });
  }

  // Check if there's a pickup event in the history
  const hasPickupEvent = events.some((event) =>
    [
      ParcelTrackingEventType.PICKUP_SCHEDULED,
      ParcelTrackingEventType.OUT_FOR_PICKUP,
      ParcelTrackingEventType.PICKED_UP,
    ].includes(event.eventType)
  );

  // If there's no pickup event, add a mock pickup event
  if (!hasPickupEvent) {
    const formattedPickupDate = pickupDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    const pickupWindow = `${pickupDate.getHours()}:00 - ${pickupDate.getHours() + 2}:00`;

    mockEvents.push({
      id: "estimated-pickup",
      eventType: ParcelTrackingEventType.PICKUP_SCHEDULED,
      eventName: "Pickup",
      eventDescription: `Scheduled for ${formattedPickupDate}, ${pickupWindow}`,
      timestamp: pickupDate.toISOString(),
    });
  }

  // If no mock events, return null
  if (mockEvents.length === 0) {
    return null;
  }

  // Render mock events
  return (
    <>
      {mockEvents.map((mockEvent) => (
        <TimelineItem key={mockEvent.id} className="h-32">
          <TimelineSeparator>
            <TimelineDot>
              <Circle className="h-4 w-4 text-gray-400" />
            </TimelineDot>
            <TimelineConnector className="bg-gray-200" />
          </TimelineSeparator>
          <TimelineContent>
            <TimelineTitle className="font-medium text-gray-400">
              {mockEvent.eventName}
            </TimelineTitle>
            {/* <TimelineDescription className="text-gray-400">
              {mockEvent.eventDescription}
            </TimelineDescription>
            {mockEvent.timestamp && (
              <TimelineDescription className="text-gray-400">
                Estimated:{" "}
                {new Date(mockEvent.timestamp).toLocaleString(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </TimelineDescription>
            )} */}
          </TimelineContent>
        </TimelineItem>
      ))}
    </>
  );
};

const getShippingAddresses = (events: ParcelTrackingEvent[]) => {
  const orderInfo = events[0]?.parcel?.order;
  return {
    pickup: orderInfo?.pickupAddress,
    delivery: orderInfo?.deliveryAddress,
  };
};

// Add this new component before the main TrackingScreen component
const RelatedPackagesCard = ({
  events,
  currentTrackingNumber,
  onTrackingNumberSelect,
}: {
  events: ParcelTrackingEvent[];
  currentTrackingNumber: string;
  onTrackingNumberSelect: (trackingNumber: string) => void;
}) => {
  const groupedEvents = useMemo(() => {
    const grouped = new Map<string, ParcelTrackingEvent>();
    events.forEach((event) => {
      // Keep only the latest event for each tracking number
      if (!grouped.has(event.trackingNumber)) {
        grouped.set(event.trackingNumber, event);
      }
    });
    return grouped;
  }, [events]);

  if (groupedEvents.size <= 1) return null;

  return (
    <Card className="p-6">
      <h3 className="mb-4 text-sm font-medium text-muted-foreground">
        Other Packages in This Shipment
      </h3>
      <div className="grid gap-3">
        {Array.from(groupedEvents.values()).map((event) => {
          const config = getStatusConfig(event.eventType);
          const isCurrentPackage =
            event.trackingNumber === currentTrackingNumber;

          return (
            <button
              key={event.trackingNumber}
              onClick={() => onTrackingNumberSelect(event.trackingNumber)}
              className={cn(
                "flex items-center justify-between rounded-lg border p-3 text-left transition-colors",
                isCurrentPackage ? "bg-muted" : "hover:bg-muted/50"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn("rounded-full p-2", config.bgColor)}>
                  <config.icon className={cn("h-4 w-4", config.textColor)} />
                </div>
                <div>
                  <div className="text-sm font-medium">
                    {event.trackingNumber}
                  </div>
                  <div className={cn("text-sm", config.textColor)}>
                    {event.eventName}
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>
    </Card>
  );
};

// Update the ImageModal component
const ImageModal = ({
  isOpen,
  onClose,
  images,
}: {
  isOpen: boolean;
  onClose: () => void;
  images: { url: string }[];
  initialIndex?: number;
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] max-w-[90vw] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
        >
          <X className="h-6 w-6" />
        </button>
        <Carousel className="w-full">
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <div className="flex items-center justify-center">
                  <img
                    src={image.url}
                    alt={`Proof ${index + 1}`}
                    className="max-h-[85vh] w-auto object-contain"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-4" />
          <CarouselNext className="right-4" />
        </Carousel>
      </div>
    </div>
  );
};

export default function TrackingScreen() {
  const { trackingNumber } = useParams<{ trackingNumber: string }>();
  const navigate = useNavigate();
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const queryClient = useQueryClient();

  // Check for existing token in localStorage
  useEffect(() => {
    if (trackingNumber) {
      const storedToken = localStorage.getItem(
        `tracking_token_${trackingNumber}`
      );

      if (storedToken) {
        // Check if token is expired
        const expiryTimeStr = localStorage.getItem(
          `tracking_token_expiry_${trackingNumber}`
        );

        if (expiryTimeStr) {
          const expiryTime = parseInt(expiryTimeStr);
          const now = Date.now();

          if (now < expiryTime) {
            // Token is still valid
            setAuthToken(storedToken);
          } else {
            // Token is expired, remove it
            console.log("Token expired, removing from localStorage");
            // Remove all tracking tokens since they're for the same order
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith("tracking_token_")) {
                localStorage.removeItem(key);
              }
            });
          }
        } else {
          // No expiry time found, assume token is valid
          // This handles backward compatibility with tokens stored before this update
          setAuthToken(storedToken);
        }
      }
    }
  }, [trackingNumber]);

  // Add token to API request
  const {
    data: events,
    isLoading,
    error: trackingError,
  } = useParcelTrackingEvents(
    trackingNumber ?? "",
    authToken ? { token: authToken } : undefined
  );

  // Check if the token is invalid (expired or unauthorized)
  useEffect(() => {
    if (trackingError) {
      const errorMessage =
        trackingError instanceof Error ? trackingError.message : "";
      // If we get an unauthorized error with a token, it means the token is invalid or expired
      if (
        authToken &&
        (errorMessage.includes("unauthorized") ||
          errorMessage.includes("Unauthorized") ||
          errorMessage.includes("401") ||
          errorMessage.includes("token"))
      ) {
        console.log("Token expired or invalid, removing from localStorage");
        // Remove all tracking tokens since they're for the same order
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("tracking_token_")) {
            localStorage.removeItem(key);
          }
        });
        setAuthToken(null);

        // Show the auth form if there's sensitive info
        if (events?.hasSensitiveInfo) {
          setShowAuthForm(true);
        }
      }
    }
  }, [trackingError, authToken, trackingNumber, events?.hasSensitiveInfo]);

  // Handle successful authentication
  const handleAuthSuccess = (token: string) => {
    setAuthToken(token);
    setShowAuthForm(false);

    // Store the token for all tracking numbers in the order
    if (events?.trackingEvents) {
      const uniqueTrackingNumbers = [
        ...new Set(events.trackingEvents.map((event) => event.trackingNumber)),
      ];
      uniqueTrackingNumbers.forEach((tn) => {
        localStorage.setItem(`tracking_token_${tn}`, token);
        // Set expiry time (e.g., 1 hour from now)
        localStorage.setItem(
          `tracking_token_expiry_${tn}`,
          (Date.now() + 3600000).toString()
        );
      });
    }

    // Invalidate the query to refetch with the new token
    void queryClient.invalidateQueries({
      queryKey: ["parcelTrackingEvents", trackingNumber],
    });

    // Also invalidate the operator location query
    void queryClient.invalidateQueries({
      queryKey: ["parcelOperatorLocation", trackingNumber],
    });
  };

  // Group events by tracking number
  const trackingNumbersWithEvents = useMemo(() => {
    if (!events?.trackingEvents)
      return new Map<string, ParcelTrackingEvent[]>();

    const groupedEvents = new Map<string, ParcelTrackingEvent[]>();
    events.trackingEvents.forEach((event) => {
      if (!groupedEvents.has(event.trackingNumber)) {
        groupedEvents.set(event.trackingNumber, []);
      }
      groupedEvents.get(event.trackingNumber)?.push(event);
    });
    return groupedEvents;
  }, [events]);

  // Get events for current tracking number
  const currentEvents =
    trackingNumbersWithEvents.get(trackingNumber ?? "") ?? [];

  // Update the state to include all images and the selected index
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    images: { url: string }[];
    initialIndex: number;
  }>({
    isOpen: false,
    images: [],
    initialIndex: 0,
  });

  // Check if there's sensitive information that requires authentication
  const hasSensitiveInfo = events?.hasSensitiveInfo;
  const needsAuthentication = hasSensitiveInfo && !authToken;

  // Add operator location query
  const {
    data: operatorLocation,
    isLoading: isLoadingOperatorLocation,
    error: operatorLocationError,
  } = useParcelLiveTracking(
    trackingNumber ?? "",
    authToken ? { token: authToken } : undefined
  );

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (
    !events?.trackingEvents ||
    events.trackingEvents.length === 0 ||
    !events.trackingEvents[0] ||
    currentEvents.length === 0
  ) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Card className="w-full max-w-md p-8 text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-lg font-medium text-muted-foreground">
            No tracking information available
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Tracking number: {trackingNumber}
          </p>
        </Card>
      </div>
    );
  }

  const latestEvent = currentEvents[0];
  // Ensure latestEvent exists before accessing its properties
  if (!latestEvent) {
    return (
      <div className="flex h-[calc(100vh-80px)] items-center justify-center">
        <Card className="w-full max-w-md p-8 text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-lg font-medium text-muted-foreground">
            No tracking event data available
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Tracking number: {trackingNumber}
          </p>
        </Card>
      </div>
    );
  }

  const statusConfig = getStatusConfig(latestEvent.eventType);

  const renderTimelineItem = (
    event: Partial<ParcelTrackingEvent> & {
      eventType: ParcelTrackingEventType;
    },
    isLast: boolean
  ) => {
    const config = getStatusConfig(event.eventType);

    // Check if this event has sensitive information that's hidden
    const hasSensitiveProof =
      event.task?.proof?.mediaItems &&
      event.task.proof.mediaItems.length === 0 &&
      event.task.proof.notes?.includes("Authentication required");

    // Check if this event should have photos but they're not visible
    const shouldHavePhotosButHidden =
      event.eventType === ParcelTrackingEventType.DELIVERED &&
      (!event.task?.proof?.mediaItems ||
        event.task.proof.mediaItems.length === 0) &&
      !hasSensitiveProof &&
      authToken;

    return (
      <TimelineItem key={event.id}>
        <TimelineSeparator>
          <TimelineDot className={cn(config.bgColor, "border-0")} />
          {!isLast && <TimelineConnector className="bg-gray-900 " />}
        </TimelineSeparator>
        <TimelineContent>
          <TimelineTitle className={cn("font-medium", config.textColor)}>
            {event.eventName}
          </TimelineTitle>
          {event.eventDescription && (
            <TimelineDescription className={config.textColor}>
              {event.eventDescription}
            </TimelineDescription>
          )}
          <TimelineDescription className={config.textColor}>
            {event.timestamp ? new Date(event.timestamp).toLocaleString() : ""}
          </TimelineDescription>

          {hasSensitiveProof && !authToken && (
            <div className="mt-2 p-3 bg-muted rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {event.task?.proof?.notes}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAuthForm(true)}
              >
                Verify Identity
              </Button>
            </div>
          )}

          {shouldHavePhotosButHidden && (
            <div className="mt-2 p-3 bg-muted rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Photos may be available but not visible. Your authentication
                  may have expired.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Clear existing token and show auth form
                  localStorage.removeItem(`tracking_token_${trackingNumber}`);
                  localStorage.removeItem(
                    `tracking_token_expiry_${trackingNumber}`
                  );
                  setAuthToken(null);
                  setShowAuthForm(true);
                }}
              >
                Re-verify Identity
              </Button>
            </div>
          )}

          {event.task?.proof?.mediaItems &&
            event.task.proof.mediaItems.length > 0 && (
              <div className="mt-2 flex gap-2 overflow-x-auto">
                {event.task.proof.mediaItems.map((media, idx) => (
                  <div
                    key={idx}
                    className="relative min-w-[100px] h-[100px] cursor-pointer transition-transform hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalState({
                        isOpen: true,
                        images: event.task?.proof?.mediaItems ?? [],
                        initialIndex: idx,
                      });
                    }}
                  >
                    <img
                      src={media.url}
                      alt={`Proof ${idx + 1}`}
                      className="h-full w-full rounded-md object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
        </TimelineContent>
      </TimelineItem>
    );
  };

  return (
    <>
      <ImageModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
        images={modalState.images}
        initialIndex={modalState.initialIndex}
      />

      {/* Authentication Modal */}
      {showAuthForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-w-md w-full">
            <TrackingAuthForm
              trackingNumber={trackingNumber ?? ""}
              onAuthSuccess={handleAuthSuccess}
            />
            <Button
              variant="ghost"
              className="mt-4 w-full bg-white"
              onClick={() => setShowAuthForm(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col">
        <BackgroundBeams />
        <div className="container mx-auto max-w-6xl relative">
          <div className="gap-6 grid grid-cols-1 lg:grid-cols-3 py-4 md:py-8 px-4 md:px-0">
            <div className="lg:col-span-2 space-y-4 md:space-y-8">
              {/* Overview Card */}
              <Card className="overflow-hidden">
                <div className="border-b bg-muted/5 px-4 sm:px-6 py-3 sm:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <div>
                      <div className="text-xs uppercase text-muted-foreground">
                        TRACKING NUMBER
                      </div>
                      <div className="text-base sm:text-lg font-semibold">
                        {trackingNumber}
                      </div>
                    </div>
                    <div
                      className={cn(
                        "rounded-full px-3 sm:px-4 py-1.5 sm:py-2 self-start sm:self-auto",
                        statusConfig.bgColor,
                        statusConfig.textColor
                      )}
                    >
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <statusConfig.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm font-medium">
                          {latestEvent.eventName}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid gap-4 sm:gap-6">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Latest Update
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                        <span className="text-xs sm:text-sm">
                          {new Date(latestEvent.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    {latestEvent.metadata?.location && (
                      <div>
                        <div className="text-sm font-medium text-muted-foreground">
                          Current Location
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                          <span className="text-xs sm:text-sm">
                            {`${latestEvent.metadata.location.city}, ${latestEvent.metadata.location.province}`}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Authentication banner */}
                    {needsAuthentication && (
                      <div className="mt-2 p-3 sm:p-4 bg-muted rounded-md">
                        <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                          <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                          <p className="text-xs sm:text-sm font-medium">
                            Some sensitive information is hidden
                          </p>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                          Verify your identity to view delivery proof photos and
                          other sensitive information.
                        </p>
                        <Button size="sm" onClick={() => setShowAuthForm(true)}>
                          Verify Identity
                        </Button>
                      </div>
                    )}

                    {/* Re-authentication banner when token exists but photos aren't visible */}
                    {authToken &&
                      !needsAuthentication &&
                      events?.hasSensitiveInfo && (
                        <div className="mt-2 p-3 sm:p-4 bg-muted rounded-md">
                          <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                            <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                            <p className="text-xs sm:text-sm font-medium">
                              Having trouble seeing photos?
                            </p>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                            If you can't see delivery photos, your
                            authentication may have expired.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              // Clear existing token and show auth form
                              localStorage.removeItem(
                                `tracking_token_${trackingNumber}`
                              );
                              localStorage.removeItem(
                                `tracking_token_expiry_${trackingNumber}`
                              );
                              setAuthToken(null);
                              setShowAuthForm(true);
                            }}
                          >
                            Re-verify Identity
                          </Button>
                        </div>
                      )}
                  </div>
                </div>
              </Card>

              {/* Add the OperatorLocation component before RelatedPackagesCard */}
              {latestEvent.eventType ===
                ParcelTrackingEventType.OUT_FOR_DELIVERY && (
                <OperatorLocation
                  data={operatorLocation}
                  isLoading={isLoadingOperatorLocation}
                  error={
                    operatorLocationError instanceof Error
                      ? operatorLocationError
                      : undefined
                  }
                  onAuthRequest={() => setShowAuthForm(true)}
                />
              )}

              {/* Timeline Card */}
              <Card className="overflow-hidden shadow-md rounded-xl">
                <div className="border-b bg-muted/5 px-4 sm:px-6 py-5">
                  <h3 className="text-lg font-semibold">Tracking History</h3>
                </div>
                <div className="p-4 sm:p-6">
                  <Timeline>
                    {/* Render mock future events */}
                    {renderMockTimelineItems(
                      latestEvent.eventType,
                      currentEvents
                    )}

                    {/* Render real events */}
                    {currentEvents
                      .filter((event) => event?.eventType) // Use optional chaining
                      .map((event, index, filteredArray) =>
                        renderTimelineItem(
                          event,
                          index === filteredArray.length - 1
                        )
                      )}
                  </Timeline>
                </div>
              </Card>

              {/* RelatedPackagesCard */}
              <RelatedPackagesCard
                events={events.trackingEvents}
                currentTrackingNumber={trackingNumber ?? ""}
                onTrackingNumberSelect={(number) =>
                  navigate(`/tracking/${number}`)
                }
              />
            </div>

            {/* Shipping Details and Overview Cards */}
            <div className="lg:col-span-1">
              <Card className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-medium">Shipping Details</h3>
                  </div>

                  <div className="grid gap-4">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        From
                      </div>
                      <div className="mt-1 text-sm">
                        {getShippingAddresses(currentEvents).pickup ? (
                          <div>
                            {/* Show sender name instead of address */}
                            <div className="mb-1">
                              {currentEvents[0]?.parcel?.order?.senderName ||
                                "Sender"}
                            </div>
                            {authToken &&
                              getShippingAddresses(currentEvents).pickup
                                .city && (
                                <div>
                                  {
                                    getShippingAddresses(currentEvents).pickup
                                      .city
                                  }
                                  ,{" "}
                                  {
                                    getShippingAddresses(currentEvents).pickup
                                      .province
                                  }
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="text-muted-foreground">
                            Not available
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        To
                      </div>
                      <div className="mt-1 text-sm">
                        {getShippingAddresses(currentEvents).delivery ? (
                          <div>
                            {/* Show recipient name */}
                            <div className="mb-1">
                              {currentEvents[0]?.parcel?.order?.recipientName ||
                                "Recipient"}
                            </div>
                            {/* Show street address only when authenticated */}
                            {authToken &&
                              getShippingAddresses(currentEvents).delivery
                                .line1 && (
                                <div className="mb-1">
                                  {
                                    getShippingAddresses(currentEvents).delivery
                                      .line1
                                  }
                                  ", "
                                  <span>
                                    {
                                      getShippingAddresses(currentEvents)
                                        .delivery.city
                                    }
                                    ,{" "}
                                    {
                                      getShippingAddresses(currentEvents)
                                        .delivery.province
                                    }
                                  </span>
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="text-muted-foreground">
                            Not available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Contact Us Card */}
              <Card className="mt-4 p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-medium">Need Help?</h3>
                  </div>

                  <div className="grid gap-4">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Customer Service
                      </div>
                      <a
                        href="tel:647-931-7960"
                        className="mt-1 flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        647-931-7960 (English, Chinese)
                      </a>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Email Support
                      </div>
                      <a
                        href="mailto:ezexpress.canada@gmail.com"
                        className="mt-1 flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        ezexpress.canada@gmail.com
                      </a>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Hours of Operation
                      </div>
                      <div className="mt-1 text-sm">
                        Monday - Sunday: 12AM - 10PM EST
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Platform Interest Card */}
              <Card className="mt-4 p-6">
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold mb-2">
                    Interested in our shipping platform?
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Get access to premium features, real-time tracking, and
                    competitive shipping rates for your business.
                  </p>
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    onClick={() => window.open("/", "_blank")}
                  >
                    Learn More
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
